import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button/Button";
import { LogoRefabricBlack } from "icons";
import userService from "api/user/user.service";
import useAuthToken from "hooks/useAuthToken";
import { useAppSelector } from "store";
import { EventDispatchLogout } from "router/events";
import { isTeamSuspendedLogin } from "utils/teamSuspended";

/**
 * The whole app while the session's team is suspended (STRIPE P5-F2, PD7; v3 P5-B11). The session is kept and explained:
 * no plan chooser, no paywall, no billing action (the owner's Portal access is an open product decision, O20).
 *
 * "Check again" is `POST /users/refresh_token` — open to a suspended team. A restored team gets a full token (stored, then a
 * reload starts the restored session cleanly); a still-suspended one gets the 402 limited answer again.
 */
const TeamSuspendedScreen = () => {
  const { t } = useTranslation();
  const email = useAppSelector((state) => state.common.user.email);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState<"still_suspended" | "check_failed" | null>(null);

  const checkAgain = async () => {
    if (checking) return;
    setChecking(true);
    setNote(null);
    try {
      const res = await userService.refreshTokenInBackground({ email: email ?? "" });
      const token = res?.responseData?.token;
      if (!token) {
        setNote("check_failed");

        return;
      }
      useAuthToken.setAuthTokenFromLocalStorage(token);
      window.location.reload();
    } catch (e) {
      setNote(isTeamSuspendedLogin(e) ? "still_suspended" : "check_failed");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen w-full bg-white pt-[30px] gap-20 px-4" data-testid="team-suspended-screen">
      <LogoRefabricBlack />
      <div className="flex flex-col items-center gap-5 max-w-[420px] w-full">
        <div className="flex flex-col items-center gap-2.5">
          <h2 className="text-h2 text-black text-center">{t("auth:team_suspended.title")}</h2>
          <span className="text-bodyMd text-grey text-center">{t("auth:team_suspended.body")}</span>
        </div>
        {note && (
          <span className="text-bodySm text-orange text-center" data-testid="team-suspended-still">
            {t(`auth:team_suspended.${note}`)}
          </span>
        )}
        <Button className="btn-primary w-full" disabled={checking} onClick={checkAgain} data-testid="team-suspended-check-again">
          {t("auth:team_suspended.check_again")}
        </Button>
        <Button className="btn-google w-full text-trueBlack/80 border border-grey-light" onClick={() => window.dispatchEvent(new CustomEvent(EventDispatchLogout))} data-testid="team-suspended-logout">
          {t("auth:team_suspended.logout")}
        </Button>
      </div>
    </div>
  );
};

export default TeamSuspendedScreen;
